import { z } from "zod";
import { request, ApiError, type RequestOptions } from "@/lib/http";

/*
 * ── The contract pattern ────────────────────────────────────────────────
 *
 * One endpoint = one exported contract object. A contract owns four things:
 *
 *   1. the HTTP method + path             (where it lives)
 *   2. a zod schema for the response      (what comes back)
 *   3. the TS type, inferred from (2)     (never hand-written)
 *   4. a query-key factory                (how the cache addresses it)
 *
 * Why zod rather than types generated straight from OpenAPI:
 *
 *   Generated types are erased at build time. They tell you what the server
 *   *claims*, and they are silent when the server lies. This API has a
 *   documented history of shape drift (renamed fields, ghost tables, columns
 *   that exist in one env and not another), and a `.map()` on an undefined
 *   field 40 lines into a component is an unreadable failure.
 *
 *   Validating at the boundary turns that into an `ApiError` of kind
 *   "contract" naming the exact field and endpoint, thrown at the call site.
 *   The cost is ~14KB gzipped and one parse per response — nothing next to a
 *   dashboard that renders live data.
 *
 *   The generated OpenAPI types still earn their place: `npm run api:types`
 *   writes them to api-schema.gen.ts, so a schema here can be diffed against
 *   what the server actually publishes. Generated types are the *check*;
 *   these schemas are the *source of truth for what this app consumes*.
 *
 * Deliberately, a contract declares only the fields this app reads. The API
 * returns far more on most endpoints; pinning all of it would mean every
 * unrelated server change breaks the dashboard.
 */

export interface Contract<TSchema extends z.ZodTypeAny, TInput = void> {
  readonly name: string;
  readonly method: NonNullable<RequestOptions["method"]>;
  /** Static path, or a builder when the input is part of the URL. */
  readonly path: (input: TInput) => string;
  /*
   * The schema is the generic, not the decoded type.
   *
   * Declaring `z.ZodType<Decoded>` looks tidier but silently breaks every
   * schema that uses .transform() or .default(): those have a different input
   * type than output type, and a one-parameter ZodType erases the input side,
   * so the assignment fails. Carrying the schema itself and reading z.infer
   * off it keeps both halves intact — and means the decoded type is never
   * hand-written anywhere.
   */
  readonly response: TSchema;
  /** Values sent as querystring (GET) or JSON body (everything else). */
  readonly params?: (input: TInput) => Record<string, unknown>;
}

export function defineContract<TSchema extends z.ZodTypeAny, TInput = void>(
  c: Contract<TSchema, TInput>,
): Contract<TSchema, TInput> {
  return c;
}

/** The decoded response type of a contract. */
export type ContractResult<C> = C extends Contract<infer S, any> ? z.infer<S> : never;

/**
 * Execute a contract: request, then validate. A 2xx whose body does not match
 * the schema is a failure — surfacing it here is the entire point.
 */
export async function call<TSchema extends z.ZodTypeAny, TInput>(
  contract: Contract<TSchema, TInput>,
  input: TInput,
  opts: Pick<RequestOptions, "signal" | "timeout" | "anonymous"> = {},
): Promise<z.infer<TSchema>> {
  const path = contract.path(input);
  const values = contract.params?.(input);
  const isRead = contract.method === "GET";

  const raw = await request<unknown>(path, {
    method: contract.method,
    ...(values && isRead ? { query: values as Record<string, string> } : {}),
    ...(values && !isRead ? { body: values } : {}),
    ...opts,
  });

  const parsed = contract.response.safeParse(raw);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .slice(0, 4)
      .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
      .join("; ");
    throw new ApiError(
      "contract",
      `${contract.name} returned an unexpected shape — ${issues}`,
      path,
      200,
      parsed.error.issues,
    );
  }
  return parsed.data;
}

/* ── Shared primitives ─────────────────────────────────────────────────── */

/** The API is inconsistent about null vs missing vs "". Normalise once. */
export const nullableString = z
  .union([z.string(), z.null()])
  .optional()
  .transform((v) => (v == null || v === "" ? undefined : v));

/** Timestamps arrive as ISO strings, epoch millis, or null depending on route. */
export const timestamp = z
  .union([z.string(), z.number(), z.null()])
  .optional()
  .transform((v) => {
    if (v == null) return undefined;
    const dt = typeof v === "number" ? new Date(v) : new Date(v);
    return Number.isNaN(dt.getTime()) ? undefined : dt;
  });

/** Several list endpoints return a bare array; others wrap it in {items}.
 *  Accept both rather than making callers care. */
export function listOf<S extends z.ZodTypeAny>(item: S) {
  /* Generic over the SCHEMA, not the decoded type — same reason as Contract
   * above: an item schema with .transform() or .default() has a different
   * input type than output type, and z.ZodType<T> erases the input side. */
  return z
    .union([
      z.array(item),
      z.object({ items: z.array(item) }),
      z.object({ data: z.array(item) }),
      z.object({ results: z.array(item) }),
    ])
    .transform((v): z.infer<S>[] =>
      Array.isArray(v) ? v : "items" in v ? v.items : "data" in v ? v.data : v.results,
    );
}

export { z };
